import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KPICard } from "@/components/KPICard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, 
  TrendingUp, 
  Eye, 
  MousePointerClick, 
  ShoppingCart,
  Zap,
  RefreshCw,
  ArrowRight
} from "lucide-react";
import { FaMeta, FaMicrosoft, FaLinkedin } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend
} from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [overviewRes, performanceRes, campaignsRes] = await Promise.all([
        fetch(`${API}/analytics/overview`, { credentials: "include" }),
        fetch(`${API}/analytics/performance?days=14`, { credentials: "include" }),
        fetch(`${API}/campaigns`, { credentials: "include" }),
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (performanceRes.ok) setPerformanceData(await performanceRes.json());
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
    } catch (error) {
      logger.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRunOptimization = async () => {
    setOptimizing(true);
    try {
      const response = await fetch(`${API}/optimization/run`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData(); // Refresh data
      } else {
        toast.error("Optimization failed");
      }
    } catch (error) {
      toast.error("Optimization failed");
    } finally {
      setOptimizing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  // Get top and bottom performers
  const sortedByRoas = [...campaigns].sort((a, b) => b.roas - a.roas);
  const topPerformers = sortedByRoas.slice(0, 3);
  const bottomPerformers = sortedByRoas.slice(-3).reverse();

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#18181B] border border-white/10 rounded-lg p-4 shadow-xl">
          <p className="text-zinc-400 text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={entry.dataKey || entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name === "ROAS" ? entry.value.toFixed(2) : formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <DashboardLayout user={user}>
      <div data-testid="dashboard-page" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl text-white font-bold mb-2">
              Dashboard
            </h1>
            <p className="text-zinc-400">
              Overview of your marketing campaign performance
            </p>
          </div>
          <Button
            data-testid="run-optimization-btn"
            onClick={handleRunOptimization}
            disabled={optimizing}
            className="bg-white text-black hover:bg-zinc-200 font-semibold rounded-lg px-6 py-2.5 transition-all active:scale-[0.98]"
          >
            {optimizing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Run Optimization
          </Button>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <KPICard
            title="Total Spend"
            value={formatCurrency(overview?.total_spend || 0)}
            icon={DollarSign}
            delay={0}
          />
          <KPICard
            title="Total Revenue"
            value={formatCurrency(overview?.total_revenue || 0)}
            icon={ShoppingCart}
            delay={0.05}
          />
          <KPICard
            title="Overall ROAS"
            value={(overview?.overall_roas || 0).toFixed(2)}
            suffix="x"
            icon={TrendingUp}
            isRoas={true}
            delay={0.1}
          />
          <KPICard
            title="Conversions"
            value={formatNumber(overview?.total_conversions || 0)}
            icon={MousePointerClick}
            delay={0.15}
          />
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <p className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Impressions</p>
            <p className="font-heading text-xl text-white font-bold tabular-nums">
              {formatNumber(overview?.total_impressions || 0)}
            </p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <p className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Clicks</p>
            <p className="font-heading text-xl text-white font-bold tabular-nums">
              {formatNumber(overview?.total_clicks || 0)}
            </p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <p className="text-xs uppercase tracking-widest text-zinc-400 mb-2">CTR</p>
            <p className="font-heading text-xl text-white font-bold tabular-nums">
              {(overview?.overall_ctr || 0).toFixed(2)}%
            </p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <p className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Avg. CPC</p>
            <p className="font-heading text-xl text-white font-bold tabular-nums">
              ${(overview?.overall_cpc || 0).toFixed(2)}
            </p>
          </motion.div>
        </div>

        {/* Platform Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: "meta", name: "Meta Ads", icon: <FaMeta className="w-5 h-5 text-[#1877F2]" />, delay: 0.4 },
            { key: "linkedin", name: "LinkedIn Ads", icon: <FaLinkedin className="w-5 h-5 text-[#0A66C2]" />, delay: 0.45 },
            { key: "google", name: "Google Ads", icon: <FcGoogle className="w-5 h-5" />, delay: 0.5 },
            { key: "bing", name: "Bing Ads", icon: <FaMicrosoft className="w-5 h-5 text-[#00A4EF]" />, delay: 0.55 },
          ].map((platform) => {
            const data = overview?.platform_breakdown?.[platform.key] || {};
            return (
              <motion.div
                key={platform.key}
                data-testid={`platform-box-${platform.key}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: platform.delay }}
              >
                <Card className="bg-[#121212] border-white/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-white text-base">
                      {platform.icon}
                      {platform.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-zinc-400 text-sm">Spend</span>
                        <span className="text-white font-medium tabular-nums text-sm">
                          {formatCurrency(data.spend || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400 text-sm">Revenue</span>
                        <span className="text-white font-medium tabular-nums text-sm">
                          {formatCurrency(data.revenue || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400 text-sm">ROAS</span>
                        <span className={`font-medium tabular-nums text-sm ${
                          (data.roas || 0) >= 1.75 ? "text-emerald-400" : "text-red-400"
                        }`}>
                          {(data.roas || 0).toFixed(2)}x
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400 text-sm">Campaigns</span>
                        <span className="text-white font-medium text-sm">
                          {data.campaigns || 0}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Performance Trend (Last 14 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={performanceData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => <span className="text-zinc-400">{value}</span>}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      name="Revenue"
                      stroke="#10B981" 
                      fill="url(#colorRevenue)"
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="spend" 
                      name="Spend"
                      stroke="#3B82F6" 
                      fill="url(#colorSpend)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Top & Bottom Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
          >
            <Card className="bg-[#121212] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  Top Performers
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate("/campaigns")}
                  className="text-zinc-400 hover:text-white"
                >
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topPerformers.map((campaign) => (
                    <div 
                      key={campaign.campaign_id}
                      onClick={() => navigate(`/campaigns/${campaign.campaign_id}`)}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/[0.07] cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {campaign.platform === "meta" ? (
                          <FaMeta className="w-4 h-4 text-[#1877F2]" />
                        ) : (
                          <FcGoogle className="w-4 h-4" />
                        )}
                        <span className="text-white text-sm truncate max-w-[180px]">
                          {campaign.name}
                        </span>
                      </div>
                      <span className="text-emerald-400 font-medium tabular-nums">
                        {campaign.roas.toFixed(2)}x
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-[#121212] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-red-400 rotate-180" />
                  Needs Attention
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate("/campaigns")}
                  className="text-zinc-400 hover:text-white"
                >
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {bottomPerformers.map((campaign) => (
                    <div 
                      key={campaign.campaign_id}
                      onClick={() => navigate(`/campaigns/${campaign.campaign_id}`)}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/[0.07] cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {campaign.platform === "meta" ? (
                          <FaMeta className="w-4 h-4 text-[#1877F2]" />
                        ) : (
                          <FcGoogle className="w-4 h-4" />
                        )}
                        <span className="text-white text-sm truncate max-w-[180px]">
                          {campaign.name}
                        </span>
                      </div>
                      <span className="text-red-400 font-medium tabular-nums">
                        {campaign.roas.toFixed(2)}x
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
